import "@app/App.css";
import { Button } from "@comps/ui/button";
import { Form } from "@comps/ui/form";
import { CompozzError, isCompozzError } from "@dto/CompozzError";
import { ObjectDto } from "@dto/objects";
import { FullRightAccess, hasGlobalRightAccess, ViewRightType } from "@dto/Users";
import { View } from "@dto/views";
import { BASE_URL } from "@utils/constants";
import { toastError } from "@utils/toasts";
import { getWsButtonStyle, TW_AppDialogButton, TW_ViewMenuItem, TW_ViewMenuItemIcon, useHover } from "@utils/twClasses";
import { del } from "@utils/utils";
import { Trash2 } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { translate } from "src/i18n/i18n";
import { useModelStore, useUserStore } from "src/store";
import { z } from "zod";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";

interface ViewMenuDeleteItemProps {
  object: ObjectDto;
  view: View;
  onViewDelete: (view: View) => void;
  closeMenu: () => void;
}

function ViewMenuDeleteItem({ object, view, onViewDelete, closeMenu }: ViewMenuDeleteItemProps) {

  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const wsHoverColor = useModelStore(state => state.wsHoverColor);
  const buttonHover = useHover(getWsButtonStyle('transparent'), getWsButtonStyle(wsHoverColor));
  const user = useUserStore(state => state.user);
  const userRole = useModelStore(state => state.userRole);
  const hasFullViewAccess = view.ownerKey === user?._key || hasGlobalRightAccess(user, userRole, ViewRightType, FullRightAccess, view._key);

  const viewDeleted = (view: View) => {
    onViewDelete(view);
    setIsDialogOpen(false);
    closeMenu();
  }

  function cancel(event: React.MouseEvent<HTMLButtonElement> | undefined) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setIsDialogOpen(false);
    closeMenu();
  }

  function click(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    if (!hasFullViewAccess) {
      return;
    }
    setIsDialogOpen(true);
  }

  function onOpenChange(open: boolean) {
    setIsDialogOpen(open);
    if (!open) {
      closeMenu();
    }
  }

  const disabled = view === null || !hasFullViewAccess;
  return (
    <Dialog open={isDialogOpen} onOpenChange={onOpenChange}>
      <DialogTrigger className={TW_ViewMenuItem + ` ${disabled ? "item-disabled" : ""}`} {...buttonHover} onClick={click}><Trash2 className={TW_ViewMenuItemIcon} />{t("view.button.delete.text")}</DialogTrigger>
      <DialogContent onOpenAutoFocus={(event) => event.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{t("view.dialog.delete.title")}</DialogTitle>
          <DialogDescription>{t("view.dialog.delete.description")}</DialogDescription>
        </DialogHeader>
        {view && <ViewDeleteForm object={object} initialView={view} onViewDelete={viewDeleted} onCancel={cancel} />}
      </DialogContent>
    </Dialog>
  )
}

interface ViewDeleteFormProps {
  object: ObjectDto;
  initialView: View;
  onViewDelete: (view: View) => void;
  onCancel: (event: React.MouseEvent<HTMLButtonElement> | undefined) => void;
}

function ViewDeleteForm({ object, initialView, onViewDelete, onCancel }: ViewDeleteFormProps) {

  const { t } = useTranslation();
  const workspace = useModelStore(state => state.selectedWorkspace);
  const formSchema = z.object({})
  const form = useForm<z.infer<typeof formSchema>>({})
  const wsColor = useModelStore(state => state.wsColor);
  const wsDarkColor = useModelStore(state => state.wsDarkColor);
  const cancelHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));
  const okHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));

  function onSubmit(values: z.infer<typeof formSchema>) {

    if (workspace === null) {
      console.error("No workspace selected");
      return;
    }

    del(`${BASE_URL}/model/workspaces/${workspace._key}/objects/${object._key}/views/${initialView._key}`, null).then(response => {
      if (response) {
        if (isCompozzError(response)) {
          const cError = response as CompozzError;
          if (cError.i18n) {
            if (cError.i18n) {
              toastError(translate(t, cError.i18n, cError.params));
            } else {
              toastError(cError.message);
            }
            onCancel(undefined);
          }
        }
      } else {
        onViewDelete(initialView);
      }
    })
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <div className="ButtonList">
          <Button className={TW_AppDialogButton} onClick={onCancel} {...cancelHover}>{t("general.button.cancel.text")}</Button>
          <Button className={TW_AppDialogButton} type="submit" {...okHover}>{t("general.button.confirm.text")}</Button>
        </div>
      </form>
    </Form>
  )
}

export default ViewMenuDeleteItem;
